import React, {useEffect, useState, useCallback, useRef} from 'react';
import Select from 'react-select';
import ApiRoute from "../../../helper/api";
import debounce from 'lodash/debounce';

export default function BrandSelect2({ assignItem, multiple = true, customDefaultValue = false, defaultValue = [], placeholder = 'انتخاب برند' }) {
    const [options, setOptions] = useState([]);
    const [selectedOptions, setSelectedOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isInitialLoaded, setIsInitialLoaded] = useState(false);
    const initialLoadDone = useRef(false);
    const selectedValuesRef = useRef(selectedOptions);
    
    useEffect(() => {
        const syncDefaultValue = async () => {
            if (defaultValue.length > 0 && !initialLoadDone.current) {
                initialLoadDone.current = true;
                if (customDefaultValue) {
                    const formattedValues = Array.isArray(defaultValue) ? defaultValue : [defaultValue];
                    setSelectedOptions(formattedValues);
                } else {
                    try {
                        setIsLoading(true);
                        const res = await ApiRoute.product.brand.getByValue.fn({
                            value: defaultValue
                        });
                        if (res?.results) {
                            const formattedValues = res.results.map(item => ({
                                ...item,
                                value: item.value,
                                label: item.label
                            }));
                            setSelectedOptions(multiple ? formattedValues : formattedValues[0]);
                        }
                    } catch (error) {
                        console.error('Error loading default values:', error);
                    } finally {
                        setIsLoading(false);
                    }
                }
            }
        };

        syncDefaultValue();
    }, []);

    useEffect(() => {
        if (JSON.stringify(selectedValuesRef.current) !== JSON.stringify(selectedOptions)) {
            selectedValuesRef.current = selectedOptions;
            assignItem(selectedOptions);
        }
    }, [selectedOptions]);

    const loadOptions = useCallback(async (inputValue = '') => {
        if (isLoading) return;
        
        try {
            setIsLoading(true);
            const res = await ApiRoute.product.brand.index.fn({
                label: inputValue
            });
            
            if (!res?.results) {
                console.error('Invalid API response format:', res);
                return;
            }

            const newOptions = res.results.map(item => ({
                ...item,
                value: item.value || item._id,
                label: item.label
            }));
            setOptions(newOptions);
            setIsInitialLoaded(true);
        } catch (error) {
            console.error('Error loading options:', error);
            setOptions([]);
        } finally {
            setIsLoading(false);
        }
    }, [isLoading]);

    const debouncedLoadOptions = useCallback(
        debounce((inputValue) => {
            if (inputValue.trim().length > 0) {
                loadOptions(inputValue);
            }
        }, 500),
        [loadOptions]
    );

    const handleInputChange = useCallback((inputValue, { action }) => {
        if (action === 'input-change') {
            debouncedLoadOptions(inputValue);
        }
        return inputValue;
    }, [debouncedLoadOptions]);

    const handleChange = useCallback((selected) => {
        const newValue = selected || (multiple ? [] : null);
        setSelectedOptions(newValue);
    }, [multiple]);

    const handleMenuOpen = useCallback(() => {
        if (!isInitialLoaded && !isLoading) {
            loadOptions('');
        }
    }, [isInitialLoaded, isLoading, loadOptions]);

    const customStyles = {
        menuPortal: (base) => ({
            ...base,
            zIndex: 9999
        }),
        control: (base) => ({
            ...base,
            minHeight: '30px',
            fontSize: '13px'
        }),
        valueContainer: (base) => ({
            ...base,
            fontSize: '13px'
        }),
        option: (base) => ({
            ...base,
            fontSize: '13px'
        }),
        singleValue: (base) => ({
            ...base,
            fontSize: '13px'
        }),
        multiValue: (base) => ({
            ...base,
            fontSize: '13px'
        }),
        input: (base) => ({
            ...base,
            fontSize: '13px'
        }),
        menu: (base) => ({
            ...base,
            fontSize: '13px'
        })
    };

    return (
        <Select
            options={options}
            value={selectedOptions}
            onChange={handleChange}
            onMenuOpen={handleMenuOpen}
            onInputChange={handleInputChange}
            isMulti={multiple}
            styles={customStyles}
            placeholder={placeholder}
            className="w-full"
            isSearchable={true}
            isClearable={true}
            filterOption={(option, input) => {
                if (!input) return true;
                return option.label.toLowerCase().includes(input.toLowerCase());
            }}
            isLoading={isLoading}
            noOptionsMessage={() => 'موردی یافت نشد'}
            getOptionValue={(option) => option.value || option._id}
            getOptionLabel={(option) => option.label}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            closeMenuOnSelect={!multiple}
            blurInputOnSelect={true}
            cacheOptions
        />
    );
}